import React, { useState, useEffect, useRef, useMemo } from "react";
import { Chip, TextField } from "@mui/material";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import * as workpalJS from "@workpal-build/workpaljs";
import axios from "axios";

import { formatDate, formatTime } from "../utils";
import config from "../config";
import EmptyState from "./ticket_list_page_component/EmptyState";

const headerHeight = "50px";

const styles = {
  chatContainer: {
    background: "white",
    display: "flex",
    height: `calc(100vh - ${headerHeight})`,
    //minHeight: "100%",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  messageContainer: {
    background: "white",
    padding: "16px",
    minHeight: 0,
    position: "relative",
  },
  messages: {
    overflowY: "scroll",
    marginTop: "54px",
    background: "white",
  },
  messageWrapper: (isUser) => ({
    display: "inline-block",
    alignSelf: isUser ? "flex-end" : "flex-start",
    backgroundColor: isUser ? "#EDF8FF" : "#F8F8F8",
    color: "#1D1D1D",
    padding: "1rem",
    margin: "0.5rem",
    borderRadius: "0.5rem",
    minWidth: "181px",
    maxWidth: "322px",
    wordBreak: "break-word",
    overflowWrap: "break-word",
    whiteSpace: "pre-wrap",
    marginLeft: isUser ? "auto" : "initial",
    borderBottomLeftRadius: isUser ? "0.5rem" : "0",
    borderBottomRightRadius: isUser ? "0" : "0.5rem",
  }),
  messageHeader: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    fontSize: "12px",
    fontWeight: 600,
  },
  inputContainer: {
    backgroundColor: "white",
    display: "flex",
    flexFlow: "row",
    height: "90px",
    padding: "16px",
    paddingBottom: "24px",
    alignItems: "center",
    borderTop: "1px solid #B3B3B3",
  },
  textField: {
    backgroundColor: "white",
    borderRadius: "4px",
    height: "44px",
    padding: "8px 16px",
    border: "1px solid #DDDDDD",
  },
  sendButton: (isActive) => ({
    width: "36px",
    height: "36px",
    color: isActive ? "#376EEE" : "#AAAAAA",
    fontSize: "1.5rem",
    marginLeft: "12px",
  }),
  msginfo: {
    textAlign: "center",
    color: "#AAAAAA",
    fontSize: "12px",
    maxWidth: "90%",
    marginLeft: "auto",
    marginRight: "auto",
  },
  noticeDivContainer: {
    width: "100vw",
    background: "white",
    padding: "16px 0px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "absolute",
    bottom: "0px",
    left: 0,
  },
  noticeDiv: {
    display: "flex",
    backgroundColor: "#FFF5E3",
    borderRadius: "4px",
    padding: "8px",
    color: "#4D4D4D",
    textAlign: "center",
    fontSize: "12px",
    fontWeight: "400",
    lineHeight: "16px",
    width: "80vw",
    maxWidth: "80%",
    alignSelf: "center",
  },
  emptyContainer: {
    display: "flex",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    background: "white",
  },
  closedMsg: {
    height: "80px",
    padding: "24px",
    fontWeight: "600",
    fontSize: "16px",
    lineHeight: "24px",
    color: "#AAAAAA",
    textAlign: "center",
  },
  chatbubbles: {
    display: "flex",
    flexFlow: "column",
  },
};

const ChatPage = ({
  status,
  chats,
  userId,
  userEmail,
  incidentId,
  setComments,
  groupedChats,
  setStatus,
  username,
  jwt,
}) => {
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [newMessage, setNewMessage] = useState("");

  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);
  const isAndroid = /Android/i.test(window.navigator.userAgent);

  // scroll to bottom of chats when page mounts
  useEffect(() => {
    const scrollContainer = messagesEndRef.current?.parentNode;
    if (scrollContainer) {
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    }
  }, [groupedChats]);

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSendMessage(newMessage);
    }
  };

  const handleKeyboardFocus = () => {
    setIsKeyboardOpen(true);
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 0);
  };

  const truncateName = (str) => {
    return str.length > 38 ? str.slice(0, 35) + "..." : str;
  };

  const handleSendMessage = async () => {
    if (newMessage.trim() === "") return;
    const newMessageData = {
      incidentId,
      userId,
      userEmail,
      user: username,
      comment: newMessage,
      datetime: new Date().toISOString(),
    };
    const updatedMessages = [...chats, newMessageData];
    setComments(updatedMessages);
    setNewMessage("");
    if (status === "Pending Response") {
      setStatus("Submitted");
    }
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 0);

    const requestData = {
      user_id: userId,
      inc_id: incidentId,
      reply_msg: newMessage,
    };

    await workpalJS.refreshAccessToken();

    try {
      await axios.post(
        `${config.API_BASE_URL}respondservicedesk`,
        requestData,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        },
      );
    } catch (error) {
      console.error("Error sending comment:", error);
    }
  };

  const showYellowNotice = useMemo(() => {
    if (!groupedChats || !userEmail) return;

    const isSubmittedOrInProgress = ["Submitted", "In Progress"].includes(
      status,
    );

    const chatsArr = Object.values(groupedChats || {}).flat();
    const lastMsg = chatsArr.length > 0 ? chatsArr[chatsArr.length - 1] : null;
    const isLastUserMsg =
      lastMsg?.userEmail?.toLowerCase() === userEmail?.toLowerCase();

    return isSubmittedOrInProgress && isLastUserMsg;
  }, [status, groupedChats, userEmail]);
  const showClosedMsg = ["Closed", "Cancelled"].includes(status);

  const noticeDivHeight = "10px";
  const keyboardOffset = "350px"; // recommended to be 200-350px (most Android keyboards)
  const messageWithoutNotice =
    isKeyboardOpen && isAndroid
      ? `calc(100vh - ${keyboardOffset} - 220px)`
      : `calc(100vh - 220px)`;
  const messageContainerHeight = !showYellowNotice
    ? messageWithoutNotice
    : `calc(${messageWithoutNotice} - ${noticeDivHeight})`;

  return (
    <div style={styles.chatContainer}>
      {!!groupedChats && (
        <div style={styles.messageContainer} ref={chatContainerRef}>
          <div
            style={{
              ...styles.messages,
              paddingBottom: showYellowNotice ? "60px" : "16px",
              height: messageContainerHeight,
            }}
          >
            {Object.keys(groupedChats).map((date, i) => {
              return (
                <div key={i} style={styles.chatbubbles}>
                  <div style={{ display: "flex", justifyContent: "center" }}>
                    <Chip
                      label={formatDate(groupedChats[date][0].datetime)}
                      size="small"
                      sx={{
                        alignSelf: "center",
                        fontFamily: "Open Sans",
                        backgroundColor: "#D0D0D0",
                        color: "#4D4D4D",
                        fontWeight: 600,
                        fontSize: "12px",
                        lineHeight: "16px",
                        margin: "12px",
                      }}
                    />
                  </div>

                  {groupedChats[date]
                    .sort((a, b) => new Date(a.datetime) - new Date(b.datetime))
                    .map((message, index) => {
                      return (
                        <div
                          key={index}
                          style={styles.messageWrapper(
                            message.userEmail.toLowerCase() ===
                              userEmail.toLowerCase(),
                          )}
                        >
                          <div style={styles.messageHeader}>
                            <span style={{ color: "#757575" }}>
                              {truncateName(message.user)}
                            </span>
                            <span
                              style={{ color: "#aaaaaa", marginLeft: "8px" }}
                            >
                              {formatTime(message.datetime)}
                            </span>
                          </div>
                          <p
                            style={{
                              margin: 0,
                              fontSize: "14px",
                              paddingTop: "5px",
                            }}
                          >
                            {message.comment}
                          </p>
                        </div>
                      );
                    })}
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {showYellowNotice && (
            <div style={styles.noticeDivContainer}>
              <div style={styles.noticeDiv}>
                IT Support is reviewing your response and will get back to you
                shortly.
              </div>
            </div>
          )}
        </div>
      )}

      {!groupedChats && (
        <div style={styles.emptyContainer}>
          <EmptyState message="Any conversations between you and the IT Support team will appear here." />
        </div>
      )}

      {!!showClosedMsg ? (
        <div style={styles.closedMsg}>
          You cannot send a message as the case is closed
        </div>
      ) : (
        <div
          style={{
            ...styles.inputContainer,
            marginBottom: isKeyboardOpen && isAndroid ? keyboardOffset : "0",
          }}
          ref={inputRef}
        >
          <TextField
            placeholder="Message"
            style={styles.textField}
            value={newMessage}
            onChange={(event) => setNewMessage(event.target.value)}
            variant="standard"
            fullWidth
            margin="none"
            onFocus={handleKeyboardFocus}
            onBlur={() => setIsKeyboardOpen(false)}
            InputProps={{
              disableUnderline: true,
            }}
            onKeyDown={handleKeyDown}
          />
          <ArrowCircleRightIcon
            color="primary"
            style={styles.sendButton(newMessage.length > 0)}
            aria-label="send message"
            onMouseDown={(e) => {
              e.preventDefault();
              handleSendMessage();
            }}
            edge="end"
          />
        </div>
      )}
    </div>
  );
};

export default ChatPage;
